import React from "react";
import { getMerchantProductDisplayValue } from "../../../../utils/merchantProductUtils";
import "./ProductCard.css";

export interface ProductCardProps {
  id: string;
  name: string;
  sku: string;
  image: string;
  merchantProduct?: {
    sku?: string;
    name?: string;
    id?: string;
  };
  store: {
    name: string;
    logo: string;
    marketplace: "shopee" | "lazada" | "tokopedia" | "bukalapak";
  };
  price: {
    amount: number;
    currency: string;
  };
  stock: {
    quantity: number;
    status: "in-stock" | "low-stock" | "out-of-stock";
  };
  engagement: {
    likes: number;
    views: number;
    rating: number;
    reviewCount: number;
  };
  quality: "good" | "incomplete" | "poor";
  syncStatus: "success" | "pending" | "failed";
  lastSyncTime: Date | string | number;
  onAction?: (action: string, id: string) => void;
  className?: string;
}

function ProductCard({
  id,
  name,
  sku,
  image,
  merchantProduct,
  store,
  price,
  stock,
  engagement,
  quality,
  syncStatus,
  lastSyncTime,
  onAction,
  className = "",
}: ProductCardProps) {
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getStockStatusClass = () => {
    switch (stock.status) {
      case "out-of-stock":
        return "tw-text-red-600 tw-bg-red-50";
      case "low-stock":
        return "tw-text-orange-600 tw-bg-orange-50";
      default:
        return "tw-text-green-600 tw-bg-green-50";
    }
  };

  const getMarketplaceIcon = () => {
    const iconMap = {
      shopee: "shopping_bag",
      lazada: "storefront",
      tokopedia: "store",
      bukalapak: "local_mall",
    };
    return iconMap[store.marketplace] || "store";
  };

  return (
    <div
      className={`tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-background-light dark:tw-bg-slate-800/60 tw-shadow-sm tw-relative tw-border tw-border-slate-200/60 dark:tw-border-slate-700/60 ${className}`}
      data-testid={`mobile-product-card-${id}`}
    >
      {/* Card Content */}
      <div className="tw-flex tw-flex-col tw-p-3">
        <div className="tw-flex tw-gap-3">
          {/* Product Image */}
          <div
            className="tw-bg-center tw-bg-no-repeat tw-aspect-square tw-bg-cover tw-rounded-lg tw-h-10 tw-w-10 tw-shrink-0 tw-shadow-sm"
            style={{ backgroundImage: `url(${image})` }}
            data-alt={`Image of ${name}`}
            data-testid="mobile-product-image"
          />

          {/* Product Details */}
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-start">
            <p
              className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white tw-line-clamp-1 tw-leading-tight"
              data-testid="mobile-product-name"
            >
              {name}
            </p>
            <a
              className="tw-mt-1 tw-flex tw-max-w-full tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary hover:tw-text-primary-dark tw-transition-colors"
              href="#"
              data-testid="mobile-merchant-product-link"
            >
              <span className="material-symbols-outlined tw-text-[14px] tw-shrink-0">
                dns
              </span>
              <span className="tw-truncate">
                {
                  getMerchantProductDisplayValue(merchantProduct, {
                    maxLength: 50,
                    prefix: "Merchant Product: ",
                  }).value
                }
              </span>
            </a>

            <div className="tw-mt-2 tw-flex tw-items-baseline tw-gap-3">
              <span
                className="tw-text-xl tw-font-bold tw-text-slate-900 dark:tw-text-white"
                data-testid="mobile-product-price"
              >
                ${price.amount.toFixed(2)}
              </span>
              <div className="tw-flex tw-items-baseline tw-gap-1 tw-text-slate-500 dark:tw-text-slate-400">
                <span className="tw-text-sm">Stock:</span>
                <span
                  className={`tw-text-lg tw-font-bold ${
                    stock.quantity === 0
                      ? "tw-text-red-500 dark:tw-text-red-400"
                      : "tw-text-slate-900 dark:tw-text-white"
                  }`}
                  data-testid="mobile-product-stock"
                >
                  {stock.quantity}
                </span>
              </div>
            </div>
            <div
              className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400 tw-mt-1"
              data-testid="mobile-store-info"
            >
              <img
                className="tw-h-3 tw-w-3 tw-rounded-sm"
                src={store.logo}
                alt={store.marketplace}
              />
              <span className="tw-truncate">
                <a
                  className="tw-font-medium tw-text-primary hover:tw-underline"
                  href="#"
                >
                  {store.name}
                </a>
              </span>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-pt-3 tw-mt-1 tw-border-t tw-border-slate-100 dark:tw-border-slate-700/50">
              {/* Engagement Metrics */}
              <div className="tw-flex tw-items-center tw-gap-3 tw-text-slate-500 dark:tw-text-slate-400">
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="material-symbols-outlined tw-text-sm">
                    favorite
                  </span>
                  <span className="tw-text-xs tw-font-medium">
                    {formatNumber(engagement.likes)}
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="material-symbols-outlined tw-text-sm">
                    visibility
                  </span>
                  <span className="tw-text-xs tw-font-medium">
                    {formatNumber(engagement.views)}
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span
                    className="material-symbols-outlined tw-text-sm"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    star
                  </span>
                  <span className="tw-text-xs tw-font-medium">
                    {engagement.rating.toFixed(1)}
                  </span>
                </div>
              </div>

              <span
                className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium ${
                  quality === "good"
                    ? "tw-bg-green-100 dark:tw-bg-green-900/50 tw-text-green-700 dark:tw-text-green-300"
                    : quality === "incomplete"
                    ? "tw-bg-orange-100 dark:tw-bg-orange-900/50 tw-text-orange-700 dark:tw-text-orange-300"
                    : "tw-bg-red-100 dark:tw-bg-red-900/50 tw-text-red-700 dark:tw-text-red-300"
                }`}
                data-testid="mobile-quality-badge"
              >
                {quality === "good" ? (
                  <span className="tw-size-1.5 tw-rounded-full tw-bg-green-500"></span>
                ) : quality === "incomplete" ? (
                  <span className="material-symbols-outlined tw-text-xs !tw-font-thin">
                    warning
                  </span>
                ) : (
                  <span className="tw-size-1.5 tw-rounded-full tw-bg-red-500"></span>
                )}
                {quality === "good"
                  ? "Good"
                  : quality === "incomplete"
                  ? "Incomplete"
                  : "Poor"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons - following HTML reference exactly */}
      <div
        className="tw-flex tw-items-center tw-gap-2 tw-border-t tw-border-slate-200 dark:tw-border-slate-700 tw-p-2 tw-bg-slate-50/50 dark:tw-bg-slate-800/30"
        data-testid="mobile-card-actions"
      >
        <button
          className="tw-flex tw-h-9 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 dark:tw-bg-primary/20 tw-px-2 tw-text-primary tw-transition-all tw-duration-200 hover:tw-bg-primary/20 hover:tw-shadow-sm active:tw-bg-primary/30"
          onClick={(e) => {
            e.stopPropagation();
            onAction?.("update-stock", id);
          }}
          data-testid="mobile-update-stock-btn"
        >
          <span className="tw-text-xs tw-font-semibold tw-leading-tight">
            Update Stock
          </span>
        </button>
        <button
          className="tw-flex tw-h-9 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 dark:tw-bg-primary/20 tw-px-2 tw-text-primary tw-transition-all tw-duration-200 hover:tw-bg-primary/20 hover:tw-shadow-sm active:tw-bg-primary/30"
          onClick={(e) => {
            e.stopPropagation();
            onAction?.("adjust-price", id);
          }}
          data-testid="mobile-adjust-price-btn"
        >
          <span className="tw-text-xs tw-font-semibold tw-leading-tight">
            Adjust Price
          </span>
        </button>
        <button
          className="tw-flex tw-h-9 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 dark:tw-bg-primary/20 tw-px-2 tw-text-primary tw-transition-all tw-duration-200 hover:tw-bg-primary/20 hover:tw-shadow-sm active:tw-bg-primary/30"
          onClick={(e) => {
            e.stopPropagation();
            onAction?.("copy-link", id);
          }}
          title="Copy Store Link"
        >
          <span className="tw-text-xs tw-font-semibold tw-leading-tight">
            Copy Link
          </span>
        </button>
        <button
          className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-slate-200 dark:tw-bg-slate-700/70 tw-text-slate-700 dark:tw-text-slate-200 tw-transition-colors hover:tw-bg-slate-300 dark:hover:tw-bg-slate-600/70"
          onClick={(e) => e.stopPropagation()}
          data-testid="mobile-more-btn"
        >
          <span className="material-symbols-outlined tw-text-lg">
            more_horiz
          </span>
        </button>
      </div>
    </div>
  );
}

export default ProductCard;
